import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardService, DashboardStats } from '../services/api';
import { API_VERSION, FRONT_VERSION } from '../version';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalProfessores: 0,
    totalTurmas: 0,
    totalDisciplinas: 0,
    totalPeriodos: 0,
    totalGradeGeradas: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      setError('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (route: string) => {
    navigate(route);
  };

  const cards = [
    {
      title: 'Professores',
      value: stats.totalProfessores,
      icon: 'fas fa-chalkboard-teacher',
      color: 'primary',
      route: '/professores',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
       title: 'Turmas',
       value: stats.totalTurmas,
       icon: 'fas fa-users',
       color: 'success',
       route: '/disciplinas',
       gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
     },
    {
      title: 'Disciplinas',
      value: stats.totalDisciplinas,
      icon: 'fas fa-book',
      color: 'info',
      route: '/disciplinas',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    {
      title: 'Períodos',
      value: stats.totalPeriodos,
      icon: 'fas fa-clock',
      color: 'warning',
      route: '/periodos',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    },
    {
      title: 'Grades',
      value: stats.totalGradeGeradas,
      icon: 'fas fa-table',
      color: 'danger',
      route: '/grades',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    }
  ];

  if (loading) {
    return (
      <div className="main-content">
        <div className="container-fluid">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="alert alert-danger" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
                <button 
                  className="btn btn-outline-danger btn-sm ms-3" 
                  onClick={loadStats}
                >
                  <i className="fas fa-redo me-1"></i>
                  Tentar Novamente
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="ps-2 text-start">
              <h1 className="h2 mb-1 text-gray-800 fw-bold">
                <i className="fas fa-tachometer-alt me-2"></i>Dashboard
              </h1>
              <p className="text-muted mb-0">Visão geral do sistema acadêmico</p>
            </div>
          </div>
        </div>

        {/* Cards de Estatísticas Modernos */}
        <div className="row g-4 mb-4" style={{ paddingLeft: '8px' }}>
          {cards.map((card, index) => (
            <div key={index} className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
              <div 
                className="card h-100 border-0 shadow-sm position-relative overflow-hidden" 
                style={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: card.gradient
                }}
                onClick={() => handleCardClick(card.route)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                }}
              >
                <div className="card-body text-white p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="flex-grow-1">
                      <h6 className="card-title text-white-50 text-uppercase fw-bold mb-2" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>
                        {card.title}
                      </h6>
                      <h2 className="mb-0 fw-bold text-white" style={{ fontSize: '2.5rem' }}>
                        {card.value}
                      </h2>
                      <p className="text-white-50 mb-0 mt-2" style={{ fontSize: '0.8rem' }}>
                        Clique para gerenciar
                      </p>
                    </div>
                    <div className="text-white-50">
                      <i className={`${card.icon} fa-2x`}></i>
                    </div>
                  </div>

                </div>
                
                {/* Efeito de brilho */}
                <div 
                  className="position-absolute top-0 start-0 w-100 h-100" 
                  style={{
                    background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.6s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(100%)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(-100%)';
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        {/* Informações do Sistema Modernizadas */}
         <div className="row" style={{ paddingLeft: '8px' }}>
           <div className="col-12">
             <div className="card border-0 shadow-sm">
               <div className="card-body p-4">
                 <div className="row align-items-center">
                   <div className="col-12">
                     <div className="text-start">
                       <h5 className="mb-2 fw-bold text-gray-800">Sistema Horus</h5>
                       <h6 className="mb-2 text-muted">Gestão Acadêmica Inteligente</h6>
                       <p className="text-muted mb-2">
                         Plataforma completa para gerenciamento de professores, turmas, disciplinas, períodos e grades horárias.
                       </p>
                       <p className="text-primary fw-bold mb-3" style={{ fontStyle: 'italic' }}>
                         Desenvolvido por professores, para professores!
                       </p>
                       
                       {/* Seção de Suporte */}
                       <div className="mt-3 pt-3 border-top">
                         <h6 className="text-muted mb-2">
                           <i className="fas fa-life-ring me-2"></i>
                           Suporte
                         </h6>
                         <div className="d-flex flex-wrap align-items-center justify-content-start gap-3">
                           <div className="d-flex align-items-center">
                             <i className="fas fa-envelope text-primary me-2"></i>
                             <a href="mailto:contato@fikra.com.br" className="text-decoration-none text-dark small">
                               contato@fikra.com.br
                             </a>
                           </div>
                           <div className="d-flex align-items-center">
                             <i className="fab fa-whatsapp text-success me-2"></i>
                             <span className="text-dark small me-2">(41) 4042-9882</span>
                             <a 
                               href="https://wa.me/5541404299882" 
                               target="_blank" 
                               rel="noopener noreferrer"
                               className="btn btn-success btn-sm px-2 py-1"
                               style={{ fontSize: '0.75rem' }}
                             >
                               <i className="fab fa-whatsapp me-1"></i>
                               WhatsApp
                             </a>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
             </div>
           </div>
         </div>

         {/* Animação Educacional */}
         <div className="row mt-4">
           <div className="col-12">
             <div className="position-relative" style={{ height: '120px', overflow: 'hidden' }}>
               {/* Livros flutuantes */}
               <div 
                 className="position-absolute" 
                 style={{
                   animation: 'float-books 8s ease-in-out infinite',
                   left: '10%',
                   top: '20px'
                 }}
               >
                 <i className="fas fa-book text-primary" style={{ fontSize: '2rem', opacity: 0.6 }}></i>
               </div>
               
               <div 
                 className="position-absolute" 
                 style={{
                   animation: 'float-books 10s ease-in-out infinite 2s',
                   left: '25%',
                   top: '40px'
                 }}
               >
                 <i className="fas fa-graduation-cap text-success" style={{ fontSize: '1.8rem', opacity: 0.5 }}></i>
               </div>
               
               <div 
                 className="position-absolute" 
                 style={{
                   animation: 'float-books 12s ease-in-out infinite 4s',
                   left: '45%',
                   top: '10px'
                 }}
               >
                 <i className="fas fa-chalkboard-teacher text-info" style={{ fontSize: '2.2rem', opacity: 0.4 }}></i>
               </div>
               
               <div 
                 className="position-absolute" 
                 style={{
                   animation: 'float-books 9s ease-in-out infinite 1s',
                   left: '65%',
                   top: '35px'
                 }}
               >
                 <i className="fas fa-users text-warning" style={{ fontSize: '1.9rem', opacity: 0.6 }}></i>
               </div>
               
               <div 
                 className="position-absolute" 
                 style={{
                   animation: 'float-books 11s ease-in-out infinite 3s',
                   left: '80%',
                   top: '15px'
                 }}
               >
                 <i className="fas fa-clock text-danger" style={{ fontSize: '1.7rem', opacity: 0.5 }}></i>
               </div>
               
               {/* Partículas de conhecimento */}
               <div 
                 className="position-absolute" 
                 style={{
                   animation: 'sparkle 6s linear infinite',
                   left: '15%',
                   top: '60px'
                 }}
               >
                 <div style={{ 
                   width: '4px', 
                   height: '4px', 
                   backgroundColor: '#007bff', 
                   borderRadius: '50%',
                   opacity: 0.7
                 }}></div>
               </div>
               
               <div 
                 className="position-absolute" 
                 style={{
                   animation: 'sparkle 8s linear infinite 2s',
                   left: '35%',
                   top: '70px'
                 }}
               >
                 <div style={{ 
                   width: '3px', 
                   height: '3px', 
                   backgroundColor: '#28a745', 
                   borderRadius: '50%',
                   opacity: 0.6
                 }}></div>
               </div>
               
               <div 
                 className="position-absolute" 
                 style={{
                   animation: 'sparkle 7s linear infinite 4s',
                   left: '55%',
                   top: '80px'
                 }}
               >
                 <div style={{ 
                   width: '5px', 
                   height: '5px', 
                   backgroundColor: '#ffc107', 
                   borderRadius: '50%',
                   opacity: 0.8
                 }}></div>
               </div>
               
               <div 
                 className="position-absolute" 
                 style={{
                   animation: 'sparkle 9s linear infinite 1s',
                   left: '75%',
                   top: '65px'
                 }}
               >
                 <div style={{ 
                   width: '4px', 
                   height: '4px', 
                   backgroundColor: '#dc3545', 
                   borderRadius: '50%',
                   opacity: 0.7
                 }}></div>
               </div>
             </div>
           </div>
         </div>
         
         {/* CSS para as animações */}
         <style>{`
           @keyframes float-books {
             0%, 100% {
               transform: translateY(0px) rotate(0deg);
             }
             25% {
               transform: translateY(-15px) rotate(2deg);
             }
             50% {
               transform: translateY(-10px) rotate(-1deg);
             }
             75% {
               transform: translateY(-20px) rotate(1deg);
             }
           }
           
           @keyframes sparkle {
             0% {
               transform: translateY(0px) scale(1);
               opacity: 0;
             }
             10% {
               opacity: 1;
             }
             50% {
               transform: translateY(-30px) scale(1.2);
               opacity: 0.8;
             }
             100% {
               transform: translateY(-60px) scale(0.8);
               opacity: 0;
             }
           }
         `}</style>
         
         {/* Rodapé com versão */}
         <div className="row mt-4">
           <div className="col-12">
             <div className="d-flex justify-content-end align-items-center" style={{ padding: '10px 0', borderTop: '1px solid #e9ecef' }}>
               <span className="text-muted small">
                 Versão: api {API_VERSION} e front {FRONT_VERSION}
               </span>
             </div>
           </div>
         </div>
      </div>
    </div>
  );
};

export default Dashboard;